import React, { useState, useEffect } from 'react';
import { AlertCircle, Check, RotateCcw, Save } from 'lucide-react';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';

interface PromptConfig {
  template: string;
  isCustom: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface PromptsData {
  preMeetingPrep: PromptConfig;
  meetingAnalysis: PromptConfig;
  availableVariables: {
    preMeetingPrep: string[];
    meetingAnalysis: string[];
  };
}

interface AIPromptConfigurationProps {
  accessToken: string;
}

export default function AIPromptConfiguration({ accessToken }: AIPromptConfigurationProps) {
  const [prompts, setPrompts] = useState<PromptsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<'pre_meeting_prep' | 'meeting_analysis' | null>(null);
  const [editedTemplate, setEditedTemplate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/system-admin/ai-prompts`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch prompts');
      }

      const data = await response.json();
      setPrompts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (promptType: 'pre_meeting_prep' | 'meeting_analysis') => {
    const template = promptType === 'pre_meeting_prep' 
      ? prompts?.preMeetingPrep.template 
      : prompts?.meetingAnalysis.template;
    
    setEditedTemplate(template || '');
    setEditingPrompt(promptType);
    setSuccessMessage(null);
    setError(null);
  };

  const handleCancel = () => {
    setEditingPrompt(null);
    setEditedTemplate('');
    setError(null);
  };

  const handleSave = async () => {
    if (!editingPrompt) return;

    try {
      setSaving(editingPrompt);
      setError(null);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/system-admin/ai-prompts`,
        {
          method: 'PUT',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            promptType: editingPrompt,
            template: editedTemplate,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save prompt');
      }

      setSuccessMessage('Prompt saved. Changes will take effect immediately.');
      setEditingPrompt(null);
      await fetchPrompts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prompt');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async (promptType: 'pre_meeting_prep' | 'meeting_analysis') => {
    if (!confirm('Are you sure you want to reset this prompt to the default? This cannot be undone.')) {
      return;
    }

    try {
      setSaving(promptType);
      setError(null);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/system-admin/ai-prompts/reset`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({ promptType }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset prompt');
      }

      setSuccessMessage('Prompt reset to default.');
      await fetchPrompts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset prompt');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading AI prompt configuration...</p>
        </div>
      </div>
    );
  }

  if (!prompts) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-900">Failed to load prompts</h3>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-gray-900">AI Prompt Configuration</h2>
        <p className="text-gray-600 mt-1">
          Customize the AI prompts used for consultant discovery tools. Changes take effect immediately.
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-green-900">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-900">{error}</p>
          </div>
        </div>
      )}

      {/* Pre-Meeting Prep Prompt */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-gray-900">Pre-Meeting Prep Prompt</h3>
            <p className="text-gray-600 text-sm mt-1">
              Used to generate talking points, agendas, and suggested attendees for discovery sessions
            </p>
            {prompts.preMeetingPrep.isCustom && (
              <p className="text-blue-600 text-sm mt-2">
                ✓ Custom prompt active • Last updated {new Date(prompts.preMeetingPrep.updatedAt!).toLocaleDateString()} by {prompts.preMeetingPrep.updatedBy}
              </p>
            )}
          </div>
          {!editingPrompt && (
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit('pre_meeting_prep')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Edit Prompt
              </button>
              {prompts.preMeetingPrep.isCustom && (
                <button
                  onClick={() => handleReset('pre_meeting_prep')}
                  disabled={saving === 'pre_meeting_prep'}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset to Default
                </button>
              )}
            </div>
          )}
        </div>

        {editingPrompt === 'pre_meeting_prep' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-2">
                Available Variables: <code className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                  {prompts.availableVariables.preMeetingPrep.map(v => `{{${v}}}`).join(', ')}
                </code>
              </label>
              <textarea
                value={editedTemplate}
                onChange={(e) => setEditedTemplate(e.target.value)}
                className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="Enter prompt template..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saving === 'pre_meeting_prep'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving === 'pre_meeting_prep' ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving === 'pre_meeting_prep'}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
              {prompts.preMeetingPrep.template}
            </pre>
          </div>
        )}
      </div>

      {/* Meeting Analysis Prompt */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-gray-900">Meeting Analysis Prompt</h3>
            <p className="text-gray-600 text-sm mt-1">
              Used to analyze meeting notes and extract Jira configuration requirements
            </p>
            {prompts.meetingAnalysis.isCustom && (
              <p className="text-blue-600 text-sm mt-2">
                ✓ Custom prompt active • Last updated {new Date(prompts.meetingAnalysis.updatedAt!).toLocaleDateString()} by {prompts.meetingAnalysis.updatedBy}
              </p>
            )}
          </div>
          {!editingPrompt && (
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit('meeting_analysis')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Edit Prompt
              </button>
              {prompts.meetingAnalysis.isCustom && (
                <button
                  onClick={() => handleReset('meeting_analysis')}
                  disabled={saving === 'meeting_analysis'}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset to Default
                </button>
              )}
            </div>
          )}
        </div>

        {editingPrompt === 'meeting_analysis' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-2">
                Available Variables: <code className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                  {prompts.availableVariables.meetingAnalysis.map(v => `{{${v}}}`).join(', ')}
                </code>
              </label>
              <textarea
                value={editedTemplate}
                onChange={(e) => setEditedTemplate(e.target.value)}
                className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="Enter prompt template..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saving === 'meeting_analysis'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving === 'meeting_analysis' ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving === 'meeting_analysis'}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
              {prompts.meetingAnalysis.template}
            </pre>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-blue-900">Tips for Editing Prompts</h4>
        <ul className="mt-2 space-y-1 text-sm text-blue-800">
          <li>• Use double curly braces for variables: <code className="bg-blue-100 px-1 rounded">{'{{variableName}}'}</code></li>
          <li>• Keep instructions clear and specific for best AI results</li>
          <li>• Test changes with real config items to verify output quality</li>
          <li>• Changes take effect immediately - no deployment needed</li>
          <li>• You can always reset to the default prompt if needed</li>
        </ul>
      </div>
    </div>
  );
}